import {
  Attachment,
  AttachmentFile,
  GuestAttachmentMap,
} from "@/types/Attachment";
import { ConditionGroup } from "@/types/EventRule";
import { EmailPreflightResult } from "@/types/AttachmentDrop";
import { PortalConfig } from "@/types/PortalConfig";
import { apiGet, apiPost, apiPut, apiDelete } from "../_apiShim";
import {
  builderIfMatchFrom,
  builderReadTicket,
  builderVersionFor,
  rememberBuilderVersion,
  settleBuilderVersion,
} from "./_builderVersionStore";

const BASE_PATH = "/api/v1/attachments/";

// ── Attachment Definition CRUD ──

export async function getAttachments(
  eventId: string,
): Promise<Attachment[]> {
  return await apiGet<Attachment[]>(`${BASE_PATH}${eventId}`, {
    "Cache-Control": "no-store",
  }, true);
}

export async function getAttachment(
  eventId: string,
  attachmentId: string,
): Promise<Attachment> {
  return await apiGet<Attachment>(`${BASE_PATH}${eventId}/${attachmentId}`, {
    "Cache-Control": "no-store",
  }, true);
}

export async function createAttachment(
  eventId: string,
  data: {
    name: string;
    assignmentType: string;
    sourceType: string;
    accessType: string;
    targetType: string;
    filters?: { key: string; operator: string; value: string | string[] }[];
    condition?: ConditionGroup;
    documentBuilderTemplateIds?: string[];
    includeCompanionCopies?: boolean;
    sortOrder?: number;
  },
): Promise<Attachment> {
  return await apiPost<Attachment>(
    `${BASE_PATH}${eventId}`,
    data,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function updateAttachment(
  eventId: string,
  attachmentId: string,
  data: {
    name?: string;
    assignmentType?: string;
    sourceType?: string;
    accessType?: string;
    targetType?: string;
    filters?: { key: string; operator: string; value: string | string[] }[];
    /** Nested AND/OR guest-display condition. Send `clearCondition: true`
     *  (with condition omitted) to $unset an existing one. */
    condition?: ConditionGroup;
    clearCondition?: boolean;
    documentBuilderTemplateIds?: string[];
    includeCompanionCopies?: boolean;
    sortOrder?: number;
  },
): Promise<Attachment> {
  return await apiPut<Attachment>(
    `${BASE_PATH}${eventId}/${attachmentId}`,
    data,
    {
      "Cache-Control": "no-store",
    },
    true,
  ) as Attachment;
}

export async function deleteAttachment(
  eventId: string,
  attachmentId: string,
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${eventId}/${attachmentId}`, {
    "Cache-Control": "no-store",
  });
}

export async function bulkDeleteAttachments(
  eventId: string,
  attachmentIds: string[],
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${eventId}/bulk-delete`,
    { attachmentIds },
    {
      "Cache-Control": "no-store",
    },
  );
}

// ── File Management ──

export async function presignFileUpload(
  eventId: string,
  attachmentId: string,
  data: { fileName: string; contentType: string; fileSize: number; guestId?: string },
): Promise<{ uploadUrl: string; s3Key: string }> {
  return await apiPost<{ uploadUrl: string; s3Key: string }>(
    `${BASE_PATH}${eventId}/${attachmentId}/files/presign`,
    data,
    { "Cache-Control": "no-store" },
    true,
  );
}

export async function confirmFileUpload(
  eventId: string,
  attachmentId: string,
  data: { s3Key: string; fileName: string; fileSize: number; contentType: string; guestId?: string },
): Promise<AttachmentFile> {
  return await apiPost<AttachmentFile>(
    `${BASE_PATH}${eventId}/${attachmentId}/files/confirm`,
    data,
    { "Cache-Control": "no-store" },
    true,
  );
}

export async function deleteFile(
  eventId: string,
  attachmentId: string,
  fileId: string,
): Promise<void> {
  await apiDelete<void>(
    `${BASE_PATH}${eventId}/${attachmentId}/files/${fileId}`,
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function getDownloadUrl(
  eventId: string,
  attachmentId: string,
  fileId: string,
): Promise<{ url: string }> {
  return await apiGet<{ url: string }>(
    `${BASE_PATH}${eventId}/${attachmentId}/files/${fileId}/download`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// ── Guest Integration ──

export async function resolveForGuests(
  eventId: string,
  guestIds: string[],
): Promise<GuestAttachmentMap> {
  return await apiPost<GuestAttachmentMap>(
    `${BASE_PATH}${eventId}/resolve-for-guests`,
    { guestIds },
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function getGuestAttachments(
  eventId: string,
  guestId: string,
): Promise<Attachment[]> {
  return await apiGet<Attachment[]>(
    `${BASE_PATH}${eventId}/guest/${guestId}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/** Send-modal attachment preflight — predicts which of the template's
 *  attachments would NOT reach this guest, and why. Warn-only dry-run. */
export async function emailPreflight(
  eventId: string,
  data: { emailTemplateId: string; guestId: string },
): Promise<EmailPreflightResult> {
  return await apiPost<EmailPreflightResult>(
    `${BASE_PATH}${eventId}/email-preflight`,
    data,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// ── Portal ──

/**
 * The mint's answer (`AttachmentResource.generatePortalLink`, a
 * `Map<String,String>`). `expiresAt` is the ISO-8601 instant the link stops
 * working — the SAME instant the JWT and the `portal_tokens` row carry — and
 * what the guest popup's portal cell prints as "valid until". Optional on
 * purpose: a pre-W3 backend (a rollback) does not send it, and the cell then
 * says "copied" without a date rather than invent one. A second mint by the
 * same cockpit user may answer with the SAME still-valid link (reuse); the
 * shape does not change.
 */
export type GeneratedPortalLink = {
  token: string;
  url: string;
  jti: string;
  expiresAt?: string;
};

export async function generatePortalLink(
  eventId: string,
  data: {
    guestId: string;
    attachmentIds?: string[];
    expiresInDays?: number;
  },
): Promise<GeneratedPortalLink> {
  return await apiPost<GeneratedPortalLink>(
    `${BASE_PATH}${eventId}/portal/generate`,
    data,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * UXA3 (PR17) — admin "Shared Links" list. Backend joins guest +
 * minter rows for display.
 */
/**
 * Where a portal token came from. MANUAL = the cockpit's "send link" dialog,
 * EMAIL = the mail pipeline's `{{portalLink}}`, EXPORT = a guest export with
 * the portal-link column. Rows written before the field existed carry none;
 * the backend derives it on read (`mintedByUserId != null` ⇒ MANUAL, else
 * EMAIL) and `portalTokenSource` repeats that rule for an older backend.
 */
export type PortalTokenSource = "MANUAL" | "EMAIL" | "EXPORT";

export const PORTAL_TOKEN_SOURCES: ReadonlyArray<PortalTokenSource> = [
  "MANUAL",
  "EMAIL",
  "EXPORT",
];

export type PortalTokenSummary = {
  jti: string;
  guestId: string;
  guestName: string;
  guestEmail: string;
  mintedByUserId: string | null;
  mintedByName: string | null;
  mintedAt: string;
  expiresAt: string;
  revokedAt: string | null;
  attachmentIds: string;
  /** Absent only from a backend that predates the field — see `portalTokenSource`. */
  source?: PortalTokenSource | null;
  /** The export job that minted the row (EXPORT rows only), hex. */
  mintedByJobId?: string | null;
};

/** Effective source of a row, legacy rule included. */
export function portalTokenSource(row: PortalTokenSummary): PortalTokenSource {
  if (row.source === "MANUAL" || row.source === "EMAIL" || row.source === "EXPORT") {
    return row.source;
  }
  return row.mintedByUserId ? "MANUAL" : "EMAIL";
}

export async function listPortalTokens(
  eventId: string,
): Promise<PortalTokenSummary[]> {
  return await apiGet<PortalTokenSummary[]>(
    `${BASE_PATH}${eventId}/portal/list`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function revokePortalToken(
  eventId: string,
  jti: string,
): Promise<{ revoked: boolean }> {
  return await apiPost<{ revoked: boolean }>(
    `${BASE_PATH}${eventId}/portal/${jti}/revoke`,
    {},
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Revoke every live token of the event — or, with `source`, only the ones of
 * that origin (`?source=EXPORT` after a leaked export file, leaving the mail
 * and hand-minted links alive). The backend busts the worker's auth cache per
 * jti on the scoped call.
 */
export async function revokeAllPortalTokens(
  eventId: string,
  source?: PortalTokenSource,
): Promise<{ revokedCount: number; source: PortalTokenSource | null }> {
  const query = source ? `?source=${encodeURIComponent(source)}` : "";
  return await apiPost<{ revokedCount: number; source: PortalTokenSource | null }>(
    `${BASE_PATH}${eventId}/portal/revoke-all${query}`,
    {},
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function getPortalConfig(
  eventId: string,
): Promise<PortalConfig> {
  const ticket = builderReadTicket(`portal-config:${eventId}`);
  const config = await apiGet<PortalConfig>(
    `${BASE_PATH}${eventId}/portal-config`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
  // W2-F (M19): remember the loaded version for the If-Match echo on save.
  rememberBuilderVersion(`portal-config:${eventId}`, (config as { version?: number })?.version, ticket);
  return config;
}

export async function updatePortalConfig(
  eventId: string,
  config: Partial<PortalConfig>,
  /**
   * Art. 50 Abs. 2 KI-VO (plan A3/D5): the AI tool behind an open in-builder
   * AI change (`apply_portal_builder_config`, the portal translate). Write-only
   * body field `aiContentTool` of the portal form — the server diffs the text
   * fields and stamps `portal_configs.aiGenerated` only on a real change.
   * Request body only; never part of the config state. Absent ⇒ the body is
   * the config object itself, as before.
   */
  aiContentTool?: string,
): Promise<void> {
  const sent = builderVersionFor(`portal-config:${eventId}`);
  await apiPut<void>(
    `${BASE_PATH}${eventId}/portal-config`,
    typeof aiContentTool === "string" && aiContentTool.length > 0 ? { ...config, aiContentTool } : config,
    {
      "Cache-Control": "no-store",
      // W2-F (M19): echo the loaded version — backend 409s stale writes.
      ...builderIfMatchFrom(sent),
    },
    true,
  );
  settleBuilderVersion(`portal-config:${eventId}`, sent);
}

